import React from 'react';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ComputerIcon from '@mui/icons-material/Computer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import wpGeodirServices from '../../services/wpGeodirServices';
import userProgramsService from '../../services/userProgramsService';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useHistory } from 'react-router-dom';
import { appRoutes } from '../../router/router';

const formatDeadline = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const s = String(dateString).trim();
  if (!s) return '';

  // Handle "#-# weeks" or "#-# week" - already has weeks, normalize to "X-Y weeks"
  const alreadyWeeksMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*weeks?$/i);
  if (alreadyWeeksMatch) return `${alreadyWeeksMatch[1]}-${alreadyWeeksMatch[2]} weeks`;

  // Handle week range format "#-#" (e.g. "1-2", "3-4") - ensure "weeks" is shown
  const weekRangeMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*$/);
  if (weekRangeMatch) return `${weekRangeMatch[1]}-${weekRangeMatch[2]} weeks`;

  // Handle "Week #-#" or "Week #-# weeks" - keep as is or ensure weeks suffix
  const weekPrefixMatch = s.match(/^week\s+(\d+)\s*-\s*(\d+)\s*(weeks?)?$/i);
  if (weekPrefixMatch) {
    const suffix = weekPrefixMatch[3] ? '' : ' weeks';
    return `Week ${weekPrefixMatch[1]}-${weekPrefixMatch[2]}${suffix}`;
  }

  try {
    const date = new Date(s);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

/** Convert duration to "# week(s)" format - exported for use in programs page */
export const formatDurationWeeks = (duration: string | null | undefined): string => {
  const s = (duration ?? '').trim();
  if (!s) return '';

  // Map known filter/API values to weeks
  const weekMap: Record<string, string> = {
    '1-4_weeks': '1-4 weeks',
    '1-3_months': '4-12 weeks',
    '3-6_months': '12-24 weeks',
    '6+_months': '24+ weeks',
    '1-4 weeks': '1-4 weeks',
    '1-3 months': '4-12 weeks',
    '3-6 months': '12-24 weeks',
    '6+ months': '24+ weeks',
  };
  if (weekMap[s]) return weekMap[s];

  // Already in "X weeks" or "X week" format
  const weekMatch = s.match(/^(\d+)\s*week(s)?$/i);
  if (weekMatch) return `${weekMatch[1]} week${Number(weekMatch[1]) === 1 ? '' : 's'}`;

  // "X-Y weeks" format
  const rangeWeekMatch = s.match(/^(\d+)\s*[-–—]\s*(\d+)\s*weeks?$/i);
  if (rangeWeekMatch) return `${rangeWeekMatch[1]}-${rangeWeekMatch[2]} weeks`;

  // Plain "X-Y" or "X–Y" (no "weeks") - assume weeks
  const plainRangeMatch = s.match(/^(\d+)\s*[-–—]\s*(\d+)\s*$/);
  if (plainRangeMatch) return `${plainRangeMatch[1]}-${plainRangeMatch[2]} weeks`;

  // "X months" - convert to weeks (1 month ≈ 4 weeks)
  const monthMatch = s.match(/^(\d+)\s*month(s)?$/i);
  if (monthMatch) {
    const n = Number(monthMatch[1]);
    const weeks = n * 4;
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }

  // "X-Y months"
  const rangeMonthMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*months?$/i);
  if (rangeMonthMatch) {
    const min = Number(rangeMonthMatch[1]) * 4;
    const max = Number(rangeMonthMatch[2]) * 4;
    return `${min}-${max} weeks`;
  }

  // Plain number - assume weeks
  const numMatch = s.match(/^\d+$/);
  if (numMatch) {
    const n = Number(numMatch[0]);
    return `${n} week${n === 1 ? '' : 's'}`;
  }

  return s;
};

const DetailRow: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="d-flex align-items-center" style={{ fontSize: '15px', color: '#111827', lineHeight: '1.5' }}>
    <span style={{ marginRight: '12px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>
    <span>{text}</span>
  </div>
);

interface ProgramCardProps {
  item: any;
  categoryName?: string;
  isSaved: boolean;
  isSaving?: boolean;
  onSaveToggle: (id: number, apiCalled?: boolean) => void;
  onViewDetail: (item: any) => void;
  showBookmark?: boolean; // Show/hide bookmark icon
  saveButtonText?: string; // Custom text for save button (default: 'Save' or 'Saved')
  isTrial?: boolean; // Whether user is on trial plan
  addManager?: boolean; // When true (and !isTrial), show "View Program Manager" instead of "Add to Program Manager"
  disableSave?: boolean; // When true (Starter at 6 saved limit), disable save button
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  item,
  categoryName,
  isSaved,
  isSaving = false,
  onSaveToggle,
  onViewDetail,
  showBookmark = true,
  saveButtonText,
  isTrial = true,
  addManager = false,
  disableSave = false,
}) => {
  const history = useHistory();
  
  // Local state to track saved status for immediate UI update
  const [localIsSaved, setLocalIsSaved] = React.useState(isSaved);
  // Local loading state for unsave operation
  const [localIsUnsaving, setLocalIsUnsaving] = React.useState(false);
  // Loading state for Add to Program Manager
  const [isAddingToManager, setIsAddingToManager] = React.useState(false);

  // Update local state when prop changes
  React.useEffect(() => {
    setLocalIsSaved(isSaved);
  }, [isSaved]);

  const handleAddToProgramManager = async () => {
    if (addManager) {
      history.push(appRoutes.summerProgramsManager.path);
      return;
    }
    setIsAddingToManager(true);
    try {
      await userProgramsService.addToProgramsManager(item.id);
      toast.success('Program added to Program Manager');
      history.push({
        pathname: appRoutes.summerProgramsDetail.path,
        search: `id=${item.id}`,
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to add program to Program Manager';
      toast.error(message);
    } finally {
      setIsAddingToManager(false);
    }
  };

  const item_title = item?.programName ?? item?.program_name ?? item?.post_title ?? '';
  const item_institution = item?.institution || item?.post_author_name || '';

  const item_location =
    [item?.city, item?.stateProvince ?? item?.state_province, item?.country].filter(Boolean).join(', ') ||
    item?.location ||
    (item?.offerIn ?? item?.offer_in) ||
    '';

  const item_delivery = (item?.programDelivery ?? item?.program_delivery) || item?.delivery || item?.format || '';
  const item_duration = item?.duration || '';

  const costMin = item?.costMin ?? item?.cost_min;
  const costMax = item?.costMax ?? item?.cost_max;
  const costRange =
    costMin != null || costMax != null
      ? [costMin, costMax].filter((v: any) => v != null && `${v}`.trim() !== '').join(' - ')
      : '';

  // Avoid mixing `??` with `||` (some Babel configs require parens)
  const item_cost = item?.cost != null ? item.cost : (costRange || item?.price || '');
  const item_skills = item?.tags || '';
  const item_deadline = formatDeadline(
    item?.applicationDeadline ?? item?.app_deadline ?? item?.application_deadline ?? item?.application_due ?? item?.deadline
  );
  const deadlineDisplay = item_deadline ? `Deadline: ${item_deadline}` : '';

  // Parse skills from comma-separated string
  const skills = item_skills
    ? item_skills.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
    : [];

  const displayValue = (v: any) => {
    const s = v == null ? '' : String(v).trim();
    return s.length > 0 ? s : 'N/A';
  };

  const handleSaveToggle = async () => {
    if (disableSave) return;
    // Prevent multiple clicks
    if (isSaving || localIsUnsaving) return;

    if (localIsSaved) {
      // Unsave program - call API directly
      setLocalIsUnsaving(true);
      try {
        await wpGeodirServices.unsaveProgram(item.id);
        // Update local state immediately for instant UI feedback
        setLocalIsSaved(false);
        toast.success('Program removed from saved list');
        // Call the callback to update parent state after successful API call
        // Pass true to indicate API was already called
        // This will update the savedPrograms state and trigger re-render
        onSaveToggle(item.id, true);
      } catch (error: any) {
        console.error('Error unsaving program:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to remove program';
        toast.error(errorMessage);
        // Revert local state on error
        setLocalIsSaved(true);
      } finally {
        setLocalIsUnsaving(false);
      }
    } else {
      // Save program - let parent handle it
      // Don't update local state here - let parent update and sync via useEffect
      onSaveToggle(item.id, false);
    }
  };

  // Generate consistent random color for a skill based on its text
  const getSkillColor = (skill: string) => {
    // Use skill text to generate consistent color
    let hash = 0;
    for (let i = 0; i < skill.length; i++) {
      hash = skill.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Predefined color palettes for badges (light backgrounds with darker text)
    const colorPalettes = [
      { bg: '#dcfce7', text: '#166534' }, // green
      { bg: '#f3e8ff', text: '#6b21a8' }, // purple
      { bg: '#fef3c7', text: '#92400e' }, // yellow/amber
      { bg: '#e0e7ff', text: '#3730a3' }, // indigo
      { bg: '#fce7f3', text: '#9f1239' }, // pink
      { bg: '#dbeafe', text: '#1e40af' }, // blue
      { bg: '#fef2f2', text: '#991b1b' }, // red
      { bg: '#ecfdf5', text: '#065f46' }, // emerald
      { bg: '#f0f9ff', text: '#0c4a6e' }, // sky
      { bg: '#f5f3ff', text: '#5b21b6' }, // violet
      { bg: '#fff7ed', text: '#9a3412' }, // orange
      { bg: '#f0fdf4', text: '#166534' }, // green variant
    ];
    
    const index = Math.abs(hash) % colorPalettes.length;
    return colorPalettes[index];
  };

  return (
    <div className="col-md-4 program-card-wrapper" style={{paddingLeft: '16px', paddingRight: '16px', paddingTop: '16px', paddingBottom: '16px'}}>
      <div 
        className="bg-white position-relative"
        style={{
          borderRadius: '12px', 
          minHeight: '420px', 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb'
        }}
      >
        {!isTrial ? (
          /* Layout for non-trial users (design) */
          <>
            {/* Top: Provider (left) + Bookmark (right) */}
            <div
              className="d-flex justify-content-between align-items-start"
              style={{ marginBottom: '8px' }}
            >
              <p
                className="mb-0"
                style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  fontWeight: '400',
                  flex: 1,
                  paddingRight: showBookmark ? '36px' : '0',
                }}
              >
                {displayValue(item_institution)}
              </p>
              {showBookmark && (
                <div
                  title={disableSave ? 'Upgrade to save more programs' : undefined}
                  style={{
                    cursor: (isSaving || localIsUnsaving || disableSave) ? 'not-allowed' : 'pointer',
                    zIndex: 1,
                    opacity: (isSaving || localIsUnsaving || disableSave) ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                  onClick={() => !(isSaving || localIsUnsaving || disableSave) && handleSaveToggle()}
                >
                  {localIsSaved ? (
                    <StarIcon style={{ color: '#eab308', fontSize: '24px' }} />
                  ) : (
                    <StarBorderIcon style={{ color: '#9ca3af', fontSize: '24px' }} />
                  )}
                </div>
              )}
            </div>
            {/* Program title */}
            <h4
              className="fw-bold mb-3"
              style={{
                fontSize: '18px',
                lineHeight: '1.3',
                color: '#111827',
                marginTop: '0',
                marginBottom: '12px',
                fontWeight: '600',
              }}
            >
              {item_title}
            </h4>
            {/* Tags: blue comma-separated text */}
            {skills.length > 0 && (
              <div
                style={{
                  fontSize: '14px',
                  color: '#2563eb',
                  lineHeight: '1.5',
                  marginBottom: '16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2px',
                }}
              >
                {skills.map((skill: string, index: number) => (
                  <span key={index}>
                    {skill}{index < skills.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
            {/* Details: Location, Format, Duration, Cost, Deadline - black icon + black text */}
            <div
              className="flex-grow-1"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                marginBottom: '20px',
              }}
            >
              <DetailRow icon={<LocationOnIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_location)} />
              <DetailRow icon={<ComputerIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_delivery)} />
              <DetailRow icon={<AccessTimeIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(formatDurationWeeks(item_duration))} />
              <DetailRow icon={<AttachMoneyIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_cost)} />
              {deadlineDisplay && (
                <DetailRow icon={<CalendarMonthIcon style={{ fontSize: '18px', color: '#111827' }} />} text={deadlineDisplay} />
              )}
            </div>
            {/* Buttons: Quick View (gray border), Details (blue border), then Add to Program Manager */}
            <div className="mt-auto" style={{ paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="btn flex-grow-1"
                  style={{
                    height: '44px',
                    fontSize: '15px',
                    fontWeight: '500',
                    backgroundColor: '#fff',
                    color: '#111827',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => onViewDetail(item)}
                >
                  Quick View
                </button>
                <button
                  type="button"
                  className="btn flex-grow-1"
                  style={{
                    height: '44px',
                    fontSize: '15px',
                    fontWeight: '500',
                    backgroundColor: '#fff',
                    color: '#2563eb',
                    border: '1px solid #2563eb',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => history.push({ pathname: appRoutes.summerProgramsDetail.path, search: `id=${item.id}` })}
                >
                  Details
                </button>
              </div>
              <button
                type="button"
                className="btn w-100 d-flex align-items-center justify-content-center"
                disabled={!addManager && isAddingToManager}
                style={{
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: '600',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: !addManager && isAddingToManager ? 'wait' : 'pointer',
                  gap: '8px',
                  opacity: !addManager && isAddingToManager ? 0.7 : 1,
                }}
                onClick={handleAddToProgramManager}
              >
                {addManager ? (
                  <VisibilityIcon style={{ fontSize: '20px' }} />
                ) : (
                  <AddIcon style={{ fontSize: '20px' }} />
                )}
                <span>
                  {!addManager && isAddingToManager
                    ? 'Adding...'
                    : addManager
                    ? 'View Program Manager'
                    : 'Add to Program Manager'}
                </span>
              </button>
            </div>
          </>
        ) : (
          /* Layout for trial users - same structure as !isTrial, 2 stacked buttons */
          <>
            {/* Top: Provider (left) + Star (right) */}
            <div
              className="d-flex justify-content-between align-items-start"
              style={{ marginBottom: '8px' }}
            >
              <p
                className="mb-0"
                style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  fontWeight: '400',
                  flex: 1,
                  paddingRight: showBookmark ? '36px' : '0',
                }}
              >
                {displayValue(item_institution)}
              </p>
              {showBookmark && (
                <div
                  title={disableSave ? 'Upgrade to save more programs' : undefined}
                  style={{
                    cursor: (isSaving || localIsUnsaving || disableSave) ? 'not-allowed' : 'pointer',
                    zIndex: 1,
                    opacity: (isSaving || localIsUnsaving || disableSave) ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                  onClick={() => !(isSaving || localIsUnsaving || disableSave) && handleSaveToggle()}
                >
                  {localIsSaved ? (
                    <StarIcon style={{ color: '#eab308', fontSize: '24px' }} />
                  ) : (
                    <StarBorderIcon style={{ color: '#9ca3af', fontSize: '24px' }} />
                  )}
                </div>
              )}
            </div>
            {/* Program title */}
            <h4
              className="fw-bold mb-3"
              style={{
                fontSize: '18px',
                lineHeight: '1.3',
                color: '#111827',
                marginTop: '0',
                marginBottom: '12px',
                fontWeight: '600',
              }}
            >
              {item_title}
            </h4>
            {/* Tags: blue comma-separated (same as !isTrial) */}
            {skills.length > 0 && (
              <div
                style={{
                  fontSize: '14px',
                  color: '#2563eb',
                  lineHeight: '1.5',
                  marginBottom: '16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2px',
                }}
              >
                {skills.map((skill: string, index: number) => (
                  <span key={index}>
                    {skill}{index < skills.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
            {/* Details: same icon + text style as !isTrial */}
            <div
              className="flex-grow-1"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                marginBottom: '20px',
              }}
            >
              <DetailRow icon={<LocationOnIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_location)} />
              <DetailRow icon={<ComputerIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_delivery)} />
              <DetailRow icon={<AccessTimeIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(formatDurationWeeks(item_duration))} />
              <DetailRow icon={<AttachMoneyIcon style={{ fontSize: '18px', color: '#111827' }} />} text={displayValue(item_cost)} />
              {deadlineDisplay && (
                <DetailRow icon={<CalendarMonthIcon style={{ fontSize: '18px', color: '#111827' }} />} text={deadlineDisplay} />
              )}
            </div>
            {/* Two buttons stacked: View Program (blue), Save (white/grey border) */}
            <div className="mt-auto" style={{ paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                type="button"
                className="btn w-100"
                style={{
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: '500',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                onClick={() => onViewDetail(item)}
              >
                View Program
              </button>
              <button
                type="button"
                className="btn w-100"
                disabled={isSaving || localIsUnsaving || disableSave}
                title={disableSave ? 'Upgrade to save more programs' : undefined}
                style={{
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: '500',
                  backgroundColor: '#fff',
                  color: '#111827',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: (isSaving || localIsUnsaving || disableSave) ? 'not-allowed' : 'pointer',
                  opacity: (isSaving || localIsUnsaving || disableSave) ? 0.6 : 1,
                }}
                onClick={() => !(isSaving || localIsUnsaving || disableSave) && handleSaveToggle()}
              >
                {disableSave ? 'Save (limit reached)' : localIsUnsaving ? 'Removing...' : isSaving ? 'Saving...' : saveButtonText || (localIsSaved ? 'Un-save' : 'Save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProgramCard;
